<template>
  <footer class="footer dinn-font">
    
    <div class="container" style="max-width: 1000px;">
      <div class="footer-grid">
        <!-- Brand Column -->
        <div style="max-width: 200px;" class="footer-column brand-column d-flex justify-content-lg-start justify-content-center align-items-center">
          <img src="@/assets/images/LOGO.svg" alt="Kame Seeds Logo" class="footer-logo mx-lg-0 mx-auto">
        </div>

        <!-- Nav Column -->
        <div class="footer-column">
          <h4 class="fs-5">Navegación</h4>
          <div class="footer-nav">
            <router-link to="/">Inicio</router-link>
            <router-link to="/productos">Catálogo</router-link>
            <router-link to="/trofeos">Trofeos</router-link>
            <!-- <router-link to="/galeria">Galería</router-link> -->
            <router-link to="/blog">Blog</router-link>
          </div>
        </div>

        <!-- Social Column -->
        <div v-if="hasContact" class="footer-column">
          <h4 class="fs-5">Redes Sociales</h4>
          <div class="social-links">
            <div class="">
                <a v-if="instagramUrl" :href="instagramUrl" target="_blank"
                  class="social-link text-decoration-none fw-light" aria-label="Instagram">
                <i class="fab fa-instagram me-2 text-blue"></i>{{ instagramHandle }}
              </a>
              <!-- twitter -->
                <a v-if="twitterUrl" :href="twitterUrl" target="_blank"
                  class="social-link text-decoration-none fw-light" aria-label="Twitter">
                <img src="@/assets/icons/x.svg" alt="X"  class="me-2 social-icon" />@{{ twitterHandle }}
              </a>
                <a v-if="email" :href="'mailto:' + email" target="_blank"
                  class="social-link text-decoration-none fw-light" aria-label="Email">
                <i class="fas fa-envelope me-2 text-blue"></i>
                {{ email }}
              </a>
                <a v-if="phone" :href="'tel:' + phone"
                  class="social-link text-decoration-none fw-light" aria-label="Teléfono">
                <i class="fas fa-phone me-2 text-blue"></i>
                {{ phone }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="footer-bottom">
        <p class="copyright fs-6 fw-light">
          © {{ currentYear }} Kame Seeds. Todos los derechos reservados.
        </p>
        <div class="developer-credit fs-6">
          <span>Desarrollado por</span>
          <a href="https://permaculture.ar" target="_blank" class="dev-link">
            <img src="@/assets/images/logo-star-white.svg" alt="Developer" class="dev-icon">
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getPublicSiteConfig } from '@/services/siteConfig'

const currentYear = computed(() => new Date().getFullYear())

const config = ref(null)

// Acepta URL completa o handle ("kame.seeds", "@kame.seeds") y arma la URL
const toUrl = (value, base) => {
  if (!value) return ''
  return /^https?:\/\//i.test(value) ? value : `${base}${String(value).replace(/^@/, '')}`
}

// De una URL o handle extrae el texto visible (ej. "kame.seeds", "KameSeeds")
const toHandle = (value) => {
  if (!value) return ''
  return String(value)
    .replace(/^https?:\/\/(www\.)?(instagram\.com|x\.com|twitter\.com)\//i, '')
    .replace(/\/+$/, '')
}

const instagramUrl = computed(() => toUrl(config.value?.instagram, 'https://instagram.com/'))
const twitterUrl = computed(() => toUrl(config.value?.twitter, 'https://x.com/'))
const instagramHandle = computed(() => toHandle(config.value?.instagram))
const twitterHandle = computed(() => toHandle(config.value?.twitter))
const email = computed(() => config.value?.email || '')
const phone = computed(() => config.value?.phone || '')
const hasContact = computed(() => !!(instagramUrl.value || twitterUrl.value || email.value || phone.value))

onMounted(async () => {
  try {
    config.value = await getPublicSiteConfig()
  } catch (e) {
    console.warn('No se pudo cargar la configuración del sitio para el footer', e)
  }
})
</script>

<style scoped>
.footer {
  position: relative;
  background-color: var(--grey-color);
  margin-top: 0;
  clear: both;
  padding: 3rem 0 1rem;
  overflow: hidden;
}

.footer-glow-1, .footer-glow-2 {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.1;
}

.footer-glow-1 {
  background: var(--primary-color);
  width: 400px;
  height: 400px;
  top: -250px;
  left: -100px;
}

.footer-glow-2 {
  background: var(--tertiary-color);
  width: 400px;
  height: 400px;
  bottom: -200px;
  right: -100px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;
  align-items: start;
}

.footer-logo {
  height: 200px;
  margin-bottom: 1.5rem;
  filter: brightness(0.9);
  transition: filter 0.3s ease;
}

.footer-logo:hover {
  filter: brightness(1);
}

.brand-description {
  color: var(--white-color-2);
  font-size: 0.9rem;
  line-height: 1.6;
}

.footer-column h4 {
  color: var(--white-color);
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
}

.footer-column {
  width: 100%;
}

.footer-nav {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer-nav a {
  color: var(--white-color-2);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  opacity: 0.8;
}

.footer-nav a:hover {
  opacity: 1;
  transform: translateX(5px);
  color: var(--primary-color);
}

.social-links {
  display: flex;
  min-width: 250px;
  gap: 1rem;
  height: fit-content;
}

.social-links > div {
  width: 100%;
}

.social-link {
  display: flex;
  align-items: center;
  width: 100%;
  margin-top: 5px;
  height: 40px;
  border-radius: 8px;
  color: var(--white-color-2);
  transition: all 0.3s ease;
  padding: 0;
  font-weight: 500;
  font-size: 14px;
}

.social-link:hover {
  color: white;
  transform: translateY(-3px);
}

.social-link i {
  font-size: 1.2rem;
}

.social-icon {
  height: 1.2rem;
  width: auto;
}

.footer-bottom {
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.copyright {
  color: var(--white-color-2);
  font-size: 0.85rem;
  opacity: 0.8;
  margin: 0;
}

.developer-credit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--white-color-2);
  font-size: 0.85rem;
  opacity: 0.8;
}

.dev-icon {
  height: 25px;
  width: auto;
  transition: transform 0.3s ease;
}

.dev-icon:hover {
  opacity: .8;
}

@media (max-width: 768px) {
  .footer-grid {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }

  .brand-column {
    padding-right: 0;
    justify-content: center;
    max-width: none !important;
  }

  .footer-logo {
    margin: 0 auto 1.5rem;
    display: block;
    max-width: 100%;
  }

  .footer-nav {
    align-items: center;
  }

  .social-links {
    justify-content: center;
    width: 100%;
  }
  .social-link{
    justify-content: center;
  }
  
  .social-links .row {
    justify-content: center;
  }

  .footer-column h4 {
    text-align: center;
  }

  .footer-bottom {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
}
</style>
